import { Commission } from "../interfaces/Commission";
import { Invoice } from "../interfaces/Invoice";
import { CommissionPayoutStatus, InvoicePayoutStatus } from "../interfaces/PayoutStatus";
import { Role } from "../interfaces/Role";
import { TransferStatus } from "../interfaces/TransferStatus";

export const asRelevantRole = (role: Role) : boolean => {
    return role as Role === Role.ADMIN || role as Role === Role.FINANCE
}

export const canMarkInvoiceAsPaidProducer = (role: Role, invoice: Invoice) : boolean => {
    return asRelevantRole(role) && isInvoicePayable(invoice.payout_status) && areAllCommissionsTransferred(invoice.commissions);
}

export const canMarkInvoiceAsPaidOperator = (role: Role, invoice: Invoice) : boolean => {
    return asRelevantRole(role) && isInvoiceConfirmed(invoice) && isInvoicePaidProducer(invoice) && areAllCommissionsPaidProducer(invoice.commissions);
}

export const isInvoicePayable = (invoicePayoutStatus: InvoicePayoutStatus) : boolean => {
    return invoicePayoutStatus === InvoicePayoutStatus.PAYABLE;
}

export const isInvoicePaidProducer = (invoice: Invoice) : boolean => {
    return invoice.payout_status === InvoicePayoutStatus.PAID_PRODUCER;
}

export const isInvoiceConfirmed = (invoice: Invoice) : boolean => {
    return invoice.status === "CONFIRMED";
}

export const isInvoiceAlreadyPaid = (invoicePayoutStatus: InvoicePayoutStatus) : boolean => {
    return invoicePayoutStatus === InvoicePayoutStatus.PAID_OPERATOR;
}

export const areAllCommissionsTransferred = (commissions: Commission[]) : boolean => { 
    return commissions
    .filter(c => c.transfer_status !== TransferStatus.TRANSFERRED_TO_OPERATOR)
    .length === 0; 
}

export const areAllCommissionsPaidProducer = (commissions: Commission[]) : boolean => {
    return commissions
        .filter(c => c.payout_status !== CommissionPayoutStatus.PAID_PRODUCER)
        .length === 0;
}

export const getTooltipValue = (role: Role, invoice: Invoice): string => {
    if (!asRelevantRole(role)) {
        return "Does not have the necessary rights to mark the invoice as paid";
    } else if (isInvoiceAlreadyPaid(invoice.payout_status)) {
        return "Already paid to operator";
    } else if (isInvoicePayable(invoice.payout_status)) {
        if (!areAllCommissionsTransferred(invoice.commissions)) {
            return "Some commissions have not been transferred";
        } else {
            return "Mark invoice and commissions as PAID_PRODUCER";
        }
    } else if (isInvoicePaidProducer(invoice)) {
        if (!isInvoiceConfirmed(invoice)) {
            return "Invoice is not CONFIRMED";
        } else if (!areAllCommissionsPaidProducer(invoice.commissions)) {
            return "Some commissions are not PAID_PRODUCER";
        } else {
            return "Mark invoice and commissions as PAID_OPERATOR";
        }
    }
    return "Unknown state";
}

export const getInvoiceValueFromString = (invoice : Invoice, element: string) : any => {
    let elementAsObjectKey = element as keyof Invoice;
    return getInvoiceValue(invoice, elementAsObjectKey);
}

export const getInvoiceValue = (invoice : Invoice, key: keyof Invoice) : any => {
    return invoice[key];
}
